import { getSupabaseWithJWT } from '@/integrations/supabase/client';
import { getCurrentProfileIdCached } from '@/services/profileService';
import type { Organization, OrganizationMember, OrgRole, OrganizationInvitation } from '@/types/organization';

export const organizationService = {
  async createOrganization(input: { name: string; slug: string; description?: string; avatar_url?: string; is_public?: boolean; }, profileId?: string): Promise<Organization> {
    const supabase = await getSupabaseWithJWT();
    
    let createdBy = profileId || (await getCurrentProfileIdCached());
    if (!createdBy) throw new Error('Profile not found. Please ensure you are logged in and your profile is set up.');

    // Use slug as provided (case-sensitive), trimming whitespace
    const providedSlug = input.slug.trim();

    const { data, error } = await (supabase as any)
      .from('organizations')
      .insert({
        name: input.name,
        slug: providedSlug,
        description: input.description || null,
        avatar_url: input.avatar_url || null,
        created_by: createdBy,
        is_public: input.is_public ?? true,
      })
      .select('id, name, slug, description, avatar_url, created_by, created_at, updated_at, is_public')
      .single();

    // Provide clearer messaging for unique-constraint conflicts (slug)
    if (error || !data) {
      // PostgREST/Supabase can surface either 23505 (PG) or 409 (HTTP)
      const code = (error as any)?.code;
      const status = (error as any)?.status || (error as any)?.statusCode;
      const message = (error as any)?.message || '';
      const details = (error as any)?.details || '';

      const isUniqueViolation =
        code === '23505' ||
        status === 409 ||
        /unique|duplicate key|violates unique constraint|organizations_slug_key/i.test(message + ' ' + details);

      if (isUniqueViolation) {
        if (/slug|organizations_slug_key/i.test(message + ' ' + details)) {
          throw new Error('An organization with this slug already exists.');
        }
        // Fallback generic unique violation
        throw new Error('An organization with this identifier already exists.');
      }
      throw error || new Error('Failed to create organization');
    }
    return data as Organization;
  },

  async listMyOrganizations(): Promise<Organization[]> {
    const supabase = await getSupabaseWithJWT();
    
    // Get current user's profile ID
    const userId = await getCurrentProfileIdCached();
    if (!userId) return [];
    
    // First get organization IDs where user is a member
    const { data: membershipData, error: membershipError } = await (supabase as any)
      .from('organization_members')
      .select('organization_id')
      .eq('user_id', userId);
    
    if (membershipError) throw membershipError;
    
    // If no memberships, return empty array
    if (!membershipData || membershipData.length === 0) return [];
    
    // Extract organization IDs
    const orgIds = membershipData.map((m: any) => m.organization_id);
    
    // Get organizations where user is a member
    const { data, error } = await (supabase as any)
      .from('organizations')
      .select('id, name, slug, description, avatar_url, created_by, created_at, updated_at, is_public')
      .in('id', orgIds)
      .order('name', { ascending: true });
    
    if (error) throw error;
    return (data || []) as Organization[];
  },

  async getOrganization(id: string): Promise<Organization | null> {
    const supabase = await getSupabaseWithJWT();
    const { data, error } = await (supabase as any).from('organizations').select('id, name, slug, description, avatar_url, created_by, created_at, updated_at, is_public').eq('id', id).maybeSingle();
    if (error) throw error;
    return (data as Organization) || null;
  },

  async updateOrganization(id: string, patch: Partial<Pick<Organization, 'name' | 'slug' | 'description' | 'avatar_url' | 'is_public'>>): Promise<Organization> {
    const supabase = await getSupabaseWithJWT();
    
    // Check user's membership and role first
    const memberships = await this.myMemberships();
    const userMembership = memberships.find(m => m.organization_id === id);
    
    if (!userMembership) {
      throw new Error(`You are not a member of organization ${id}`);
    }
    
    if (!['owner', 'admin'].includes(userMembership.role)) {
      throw new Error(`Insufficient permissions. Required: owner or admin, have: ${userMembership.role}`);
    }
    
    // If updating slug, validate
    if (patch.slug && patch.slug.trim()) {
      const newSlug = patch.slug.trim();
      
      // Validate slug format (letters and numbers only; case-sensitive)
      if (!/^[A-Za-z0-9]+$/.test(newSlug)) {
        throw new Error('Slug must contain only letters and numbers');
      }
      
      // Use the slug as provided
      patch.slug = newSlug;
    }

    const { data, error } = await (supabase as any)
      .from('organizations')
      .update({ ...patch })
      .eq('id', id)
      .select('id, name, slug, description, avatar_url, created_by, created_at, updated_at, is_public')
      .single();
    
    if (error) {
      throw error;
    }
    
    if (!data) {
      throw new Error('Failed to update organization - no data returned');
    }
    
    return data as Organization;
  },

  async deleteOrganization(id: string): Promise<void> {
    const supabase = await getSupabaseWithJWT();
    const { error } = await (supabase as any).from('organizations').delete().eq('id', id);
    if (error) throw error;
  },

  async listMembers(orgId: string): Promise<any[]> {
    const supabase = await getSupabaseWithJWT();
    const { data, error } = await (supabase as any).rpc('get_organization_members_with_profiles', {
      p_org_id: orgId,
    });
    if (error) throw error;

    const members = (data || []).map((row: any) => ({
      organization_id: row.organization_id,
      user_id: row.user_id,
      role: row.role,
      invited_by: row.invited_by,
      created_at: row.created_at,
      updated_at: row.updated_at,
      profiles: row.profile_id ? {
        id: row.profile_id,
        display_name: row.display_name,
        avatar_url: row.avatar_url,
        email: row.email,
      } : null,
    })) as any[];

    const missingProfileIds = [
      ...new Set(
        members
          .filter((member) => !member.profiles && member.user_id)
          .map((member) => member.user_id)
      ),
    ];

    if (missingProfileIds.length > 0) {
      const { data: safeProfiles } = await (supabase as any)
        .from('profiles')
        .select('id, display_name, avatar_url, username')
        .in('id', missingProfileIds);

      const profilesById = new Map(
        (safeProfiles || []).map((profile: any) => [
          profile.id,
          {
            id: profile.id,
            display_name: profile.display_name || profile.username || null,
            avatar_url: profile.avatar_url,
            email: null,
          },
        ])
      );

      members.forEach((member) => {
        if (!member.profiles) {
          member.profiles = profilesById.get(member.user_id) || null;
        }
      });
    }

    return members;
  },

  async addMember(orgId: string, userId: string, role: OrgRole): Promise<void> {
    const supabase = await getSupabaseWithJWT();
    const { error } = await (supabase as any).from('organization_members').insert({ organization_id: orgId, user_id: userId, role });
    if (error) throw error;
  },

  async removeMember(orgId: string, userId: string): Promise<void> {
    const supabase = await getSupabaseWithJWT();
    const { error } = await (supabase as any).from('organization_members').delete().match({ organization_id: orgId, user_id: userId });
    if (error) throw error;
  },

  async updateMemberRole(orgId: string, userId: string, role: OrgRole): Promise<void> {
    const supabase = await getSupabaseWithJWT();
    const currentUserId = await getCurrentProfileIdCached();
    if (!currentUserId) throw new Error('You must be logged in to change member roles.');

    const { data: currentUserMembership, error: membershipError } = await supabase
      .from('organization_members')
      .select('role')
      .eq('organization_id', orgId)
      .eq('user_id', currentUserId)
      .single();

    if (membershipError || !currentUserMembership) {
      throw new Error('You are not a member of this organization.');
    }

    if (currentUserMembership.role !== 'owner') {
      throw new Error('Only owners can change member roles.');
    }

    const { error } = await (supabase as any).from('organization_members').update({ role }).match({ organization_id: orgId, user_id: userId });
    if (error) throw error;
  },

  async inviteMember(orgId: string, email: string, role: OrgRole): Promise<OrganizationInvitation> {
    const supabase = await getSupabaseWithJWT();
    const token = crypto.randomUUID();
    const { data, error } = await (supabase as any)
      .from('organization_invitations')
      .insert({ organization_id: orgId, email, role, token })
      .select('*')
      .single();
    if (error || !data) throw error || new Error('Failed to create invitation');
    
    // Send invitation email using API endpoint
    try {
      const org = await this.getOrganization(orgId);
      if (org) {
        await this.sendInvitationEmail(org, email, role, token);
      }
    } catch (emailError) {
      console.error('Error sending invitation email:', emailError);
      // Don't throw here - the invitation was created successfully
    }
    
    return data as OrganizationInvitation;
  },

  async resendInvite(inviteId: string): Promise<void> {
    const supabase = await getSupabaseWithJWT();
    
    // Get the invitation details
    const { data: invite, error } = await (supabase as any)
      .from('organization_invitations')
      .select('*')
      .eq('id', inviteId)
      .single();
    
    if (error || !invite) throw error || new Error('Invitation not found');
    
    // Get organization details
    const org = await this.getOrganization(invite.organization_id);
    if (!org) throw new Error('Organization not found');
    
    // Send the invitation email again
    await this.sendInvitationEmail(org, invite.email, invite.role, invite.token);
  },

  async sendInvitationEmail(org: any, email: string, role: OrgRole, token: string): Promise<void> {
    const inviteUrl = `${window.location.origin}/o/${org.slug}/invite/${token}`;
    
    // Create HTML email content for better formatting
    const emailContent = `
      <div style="max-width: 600px; margin: 0 auto; padding: 20px; font-family: Arial, sans-serif;">
        <h1 style="color: #333; text-align: center;">You're Invited!</h1>
        <p>Hi there,</p>
        <p>You've been invited to join <strong>${org.name}</strong> as a ${role} on CalOne Events.</p>
        <div style="text-align: center; margin: 30px 0;">
          <a href="${inviteUrl}" style="background-color: #007bff; color: white; padding: 12px 24px; text-decoration: none; border-radius: 6px; display: inline-block;">Accept Invitation</a>
        </div>
        <p>Or copy and paste this link in your browser:</p>
        <p style="word-break: break-all; color: #666;">${inviteUrl}</p>
        <p>Best regards,<br>The CalOne Events Team</p>
      </div>
    `;

    const subject = `You're invited to join ${org.name} on CalOne Events`;
    
    try {
      const response = await fetch('https://v1.calone.com/lovableapi/send-caloneevents-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        mode: 'cors',
        body: JSON.stringify({
          to: [email],
          subject: subject,
          content: emailContent
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(`Email API request failed: ${response.status} ${response.statusText}. ${errorData.error || ''}`);
      }

      const result = await response.json();
      if (!result.success) {
        throw new Error(`Email sending failed: ${result.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Failed to send organization invitation email:', error);
      throw new Error(`Failed to send invitation email: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  async listInvites(orgId: string): Promise<OrganizationInvitation[]> {
    const supabase = await getSupabaseWithJWT();
    const { data, error } = await (supabase as any)
      .from('organization_invitations')
      .select('*')
      .eq('organization_id', orgId)
      .order('created_at', { ascending: false });
    if (error) throw error;
    return (data || []) as OrganizationInvitation[];
  },

  async myMemberships(): Promise<{ organization_id: string; role: OrgRole }[]> {
    const supabase = await getSupabaseWithJWT();
    const userId = await getCurrentProfileIdCached();
    if (!userId) return [];
    const { data, error } = await (supabase as any)
      .from('organization_members')
      .select('organization_id, role')
      .eq('user_id', userId);
    if (error) throw error;
    return (data || []) as { organization_id: string; role: OrgRole }[];
  },

  // Resolve the current user's role for an organization, identified by either
  // its id or its slug. Returns null when the user is not a member.
  async getMyRoleForOrg(orgIdOrSlug: string): Promise<OrgRole | null> {
    const memberships = await this.myMemberships();
    if (memberships.length === 0) return null;

    let membership = memberships.find(m => m.organization_id === orgIdOrSlug);
    if (!membership) {
      // Not an id match — resolve a slug against the user's own organizations.
      const orgs = await this.listMyOrganizations();
      const org = orgs.find(o => o.slug === orgIdOrSlug || o.id === orgIdOrSlug);
      if (!org) return null;
      membership = memberships.find(m => m.organization_id === org.id);
    }
    return membership?.role ?? null;
  },

  async acceptInvite(token: string): Promise<void> {
    const supabase = await getSupabaseWithJWT();
    const { data, error } = await (supabase as any)
      .rpc('accept_organization_invitation', { invitation_token: token });
    
    if (error) throw error;
    
    if (!data.success) {
      throw new Error(data.error || 'Failed to accept invitation');
    }
  },

  async revokeInvite(inviteId: string): Promise<void> {
    const supabase = await getSupabaseWithJWT();
    const { error } = await (supabase as any).from('organization_invitations').delete().eq('id', inviteId);
    if (error) throw error;
  },

  async searchInvitableProfiles(orgId: string, query: string): Promise<{ id: string; display_name: string | null; avatar_url: string | null }[]> {
    const q = (query || '').trim();
    if (q.length < 2) return [];
    const supabase = await getSupabaseWithJWT();
    const { data, error } = await (supabase as any).rpc('search_invitable_profiles', {
      p_query: q,
      p_org_id: orgId,
      p_limit: 8,
    });
    if (error) throw error;
    return (data || []) as any[];
  },

  async addExistingUserToOrg(orgId: string, userId: string, role: OrgRole): Promise<void> {
    const supabase = await getSupabaseWithJWT();
    const { data, error } = await (supabase as any).rpc('add_existing_user_to_org', {
      p_org_id: orgId,
      p_user_id: userId,
      p_role: role,
    });
    if (error) throw error;
    if (!data?.success) throw new Error(data?.error || 'Failed to add user');
  },

  // Self-serve, reusable join links. member_token grants 'member', admin_token
  // grants 'admin'. Only owners/admins can read or rotate them.
  async getOrgInviteLinks(orgId: string): Promise<{ member_token: string; admin_token: string }> {
    const supabase = await getSupabaseWithJWT();
    const { data, error } = await (supabase as any).rpc('get_organization_invite_links', { p_org_id: orgId });
    if (error) throw error;
    const row = Array.isArray(data) ? data[0] : data;
    if (!row) throw new Error('Failed to load invite links');
    return { member_token: row.member_token, admin_token: row.admin_token };
  },

  async regenerateOrgInviteLinks(orgId: string): Promise<{ member_token: string; admin_token: string }> {
    const supabase = await getSupabaseWithJWT();
    const { data, error } = await (supabase as any).rpc('regenerate_organization_invite_links', { p_org_id: orgId });
    if (error) throw error;
    const row = Array.isArray(data) ? data[0] : data;
    if (!row) throw new Error('Failed to regenerate invite links');
    return { member_token: row.member_token, admin_token: row.admin_token };
  },

  async acceptOrgJoin(token: string): Promise<{ org_slug: string; granted_role: OrgRole }> {
    const supabase = await getSupabaseWithJWT();
    const { data, error } = await (supabase as any).rpc('accept_organization_join', { p_token: token });
    if (error) throw error;
    const row = Array.isArray(data) ? data[0] : data;
    if (!row) throw new Error('Failed to accept invite');
    return { org_slug: row.org_slug, granted_role: row.granted_role as OrgRole };
  },

  // Step down from admin to plain member (acts on your own membership only).
  async leaveOrganizationAdmin(orgId: string): Promise<void> {
    const supabase = await getSupabaseWithJWT();
    const { error } = await (supabase as any).rpc('leave_organization_admin', { p_org_id: orgId });
    if (error) throw error;
  },

  // Leave the organization entirely (removes your own membership + group-admin
  // roles for this org). The owner cannot leave; transfer ownership first.
  async leaveOrganization(orgId: string): Promise<void> {
    const supabase = await getSupabaseWithJWT();
    const { error } = await (supabase as any).rpc('leave_organization', { p_org_id: orgId });
    if (error) throw error;
  },
};
